import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, FileText, Wrench, Users, Settings, Eye, EyeOff, Plus, Pencil, Trash2, X, Upload, Link2, Copy, Check, Globe, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";

const iconMap = { wrench: Wrench, users: Users, settings: Settings, "file-text": FileText, tool: Wrench, default: BookOpen };
const getIcon = (name) => iconMap[name] || iconMap.default;
const ICON_OPTIONS = ["wrench", "users", "settings", "file-text", "tool"];

const PUBLIC_BASE = window.location.origin;

const ManualsPage = () => {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editManual, setEditManual] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [copiedLib, setCopiedLib] = useState(false);

  const fetchManuals = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/manuals`); setManuals(res.data); }
    catch { toast.error("Errore caricamento manuali"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchManuals(); }, []);

  const toggleDealerVisibility = async (manual) => {
    try {
      await axios.put(`${API}/manuals/${manual.id}/visibility`, { visible_to_dealers: !manual.visible_to_dealers });
      toast.success(manual.visible_to_dealers ? "Nascosto ai dealer" : "Visibile ai dealer");
      fetchManuals();
    } catch { toast.error("Errore"); }
  };

  const togglePublic = async (manual) => {
    try {
      await axios.put(`${API}/manuals/${manual.id}/visibility`, { is_public: !manual.is_public });
      toast.success(manual.is_public ? "Manuale reso privato" : "Manuale reso pubblico");
      fetchManuals();
    } catch { toast.error("Errore"); }
  };

  const deleteManual = async (manual) => {
    if (!window.confirm(`Eliminare "${manual.title}"?`)) return;
    try {
      await axios.delete(`${API}/manuals/${manual.id}`);
      toast.success("Manuale eliminato");
      fetchManuals();
    } catch { toast.error("Errore eliminazione"); }
  };

  const copyLink = (manual) => {
    if (!manual.is_public) {
      toast.warning("Questo manuale e privato. Rendilo pubblico prima di condividere il link.", { duration: 4000 });
      return;
    }
    navigator.clipboard.writeText(`${PUBLIC_BASE}/manuali/${manual.id}`);
    setCopiedId(manual.id);
    toast.success("Link copiato!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const copyLibraryLink = () => {
    navigator.clipboard.writeText(`${PUBLIC_BASE}/manuali`);
    setCopiedLib(true);
    toast.success("Link libreria copiato!");
    setTimeout(() => setCopiedLib(false), 2000);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in" data-testid="admin-manuals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Libreria Manuali</h1>
          <p className="text-gray-400 text-sm mt-1">{manuals.length} manuali | Gestisci contenuti e visibilita</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/admin/manuals/svgs-status")} className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-gray-700 rounded-lg text-sm text-gray-300 hover:border-gray-500 transition-colors" data-testid="link-svgs-status" title="Stato SVG dei manuali operativi (localizzati per 18 SOC lingue)">
            <FileText size={16} className="text-lojack-red" />
            Stato SVG Manuali
          </button>
          <button onClick={copyLibraryLink} className="flex items-center gap-2 px-3 py-2 bg-[#1A1A1A] border border-gray-700 rounded-lg text-sm text-gray-300 hover:border-gray-500 transition-colors" data-testid="copy-library-link">
            {copiedLib ? <Check size={16} className="text-lojack-blue-400" /> : <Link2 size={16} className="text-lojack-red" />}
            {copiedLib ? "Copiato!" : "Link Pubblico Libreria"}
          </button>
          <a href="/manuali" target="_blank" rel="noreferrer" className="p-2 bg-[#1A1A1A] border border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors" title="Apri pagina pubblica" data-testid="open-public-library">
            <ExternalLink size={16} />
          </a>
          <button onClick={() => { setEditManual(null); setShowModal(true); }} className="btn-lojack flex items-center gap-2" data-testid="add-manual-btn">
            <Plus size={18} /> Nuovo Manuale
          </button>
        </div>
      </div>

      {/* Manual Cards */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : (
        <div className="space-y-3">
          {manuals.map(m => {
            const Icon = getIcon(m.icon);
            return (
              <div key={m.id} className="wallet-card" data-testid={`admin-manual-card-${m.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Left: Icon + Info */}
                  <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/admin/manuals/${m.id}`)}>
                    <div className="w-12 h-12 rounded-xl bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center flex-shrink-0">
                      <Icon size={22} className="text-lojack-red" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-white font-bold truncate">{m.title}</h3>
                      <p className="text-gray-500 text-xs truncate">{m.description}</p>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                    {/* Public badge */}
                    <button onClick={() => togglePublic(m)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${m.is_public ? "bg-lojack-blue-500/20 text-lojack-blue-400 hover:bg-lojack-blue-500/30" : "bg-gray-700/50 text-gray-500 hover:bg-gray-700"}`} title={m.is_public ? "Pubblico: visibile a tutti" : "Privato: richiede login"} data-testid={`toggle-public-${m.id}`}>
                      <Globe size={12} /> {m.is_public ? "Pubblico" : "Privato"}
                    </button>

                    {/* Dealer visibility */}
                    <button onClick={() => toggleDealerVisibility(m)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${m.visible_to_dealers ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" : "bg-gray-700/50 text-gray-500 hover:bg-gray-700"}`} data-testid={`toggle-dealer-${m.id}`}>
                      {m.visible_to_dealers ? <Eye size={12} /> : <EyeOff size={12} />}
                      Dealer
                    </button>

                    {/* Copy share link */}
                    <button onClick={() => copyLink(m)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors" data-testid={`copy-link-${m.id}`}>
                      {copiedId === m.id ? <Check size={12} className="text-lojack-blue-400" /> : <Copy size={12} />}
                      {copiedId === m.id ? "Copiato!" : "Copia Link"}
                    </button>

                    {/* Edit */}
                    <button onClick={() => { setEditManual(m); setShowModal(true); }} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-colors" data-testid={`edit-manual-${m.id}`}>
                      <Pencil size={14} />
                    </button>

                    {/* Delete */}
                    <button onClick={() => deleteManual(m)} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-gray-800 transition-colors" data-testid={`delete-manual-${m.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <ManualFormModal
          manual={editManual}
          onClose={() => { setShowModal(false); setEditManual(null); }}
          onSaved={() => { setShowModal(false); setEditManual(null); fetchManuals(); }}
        />
      )}
    </div>
  );
};

const ManualFormModal = ({ manual, onClose, onSaved }) => {
  const isEdit = !!manual;
  const [form, setForm] = useState({
    title: manual?.title || "",
    description: manual?.description || "",
    icon: manual?.icon || "file-text",
    visible_to_dealers: manual?.visible_to_dealers || false,
    is_public: manual?.is_public || false,
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let pdf_url = manual?.pdf_url || "";

      // Upload PDF if new file selected
      if (pdfFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", pdfFile);
        const uploadRes = await axios.post(`${API}/manuals/upload-pdf`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        pdf_url = uploadRes.data.url;
        setUploading(false);
      }

      const payload = { ...form, pdf_url };

      if (isEdit) {
        await axios.put(`${API}/manuals/${manual.id}`, payload);
        toast.success("Manuale aggiornato");
      } else {
        await axios.post(`${API}/manuals`, payload);
        toast.success("Manuale creato");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{ width: "100vw", height: "100vh", top: 0, left: 0 }} onClick={onClose} data-testid="manual-form-modal">
      <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-xl border border-gray-700 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-700 shrink-0">
          <h2 className="text-lg font-bold text-white">{isEdit ? "Modifica Manuale" : "Nuovo Manuale"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="text-gray-400 text-xs font-medium">Titolo *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" required data-testid="manual-form-title" />
          </div>

          <div>
            <label className="text-gray-400 text-xs font-medium">Descrizione</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none resize-none" data-testid="manual-form-description" />
          </div>

          <div>
            <label className="text-gray-400 text-xs font-medium">Icona</label>
            <div className="flex gap-2 mt-1">
              {ICON_OPTIONS.map(ic => {
                const Ic = getIcon(ic);
                return (
                  <button key={ic} type="button" onClick={() => setForm({ ...form, icon: ic })} className={`w-10 h-10 rounded-lg flex items-center justify-center border transition-colors ${form.icon === ic ? "border-lojack-red bg-lojack-red/10" : "border-gray-700 bg-[#111] hover:border-gray-500"}`}>
                    <Ic size={18} className={form.icon === ic ? "text-lojack-red" : "text-gray-400"} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-xs font-medium">File PDF {!isEdit && "*"}</label>
            <div className="mt-1 border border-dashed border-gray-700 rounded-xl p-4 text-center bg-[#111] hover:border-gray-500 transition-colors">
              <input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} className="hidden" id="pdf-upload" data-testid="manual-form-pdf" />
              <label htmlFor="pdf-upload" className="cursor-pointer">
                <Upload size={24} className="text-gray-500 mx-auto mb-2" />
                {pdfFile ? (
                  <p className="text-lojack-blue-400 text-sm">{pdfFile.name}</p>
                ) : manual?.pdf_url ? (
                  <p className="text-gray-500 text-sm">PDF attuale caricato. Clicca per sostituire.</p>
                ) : (
                  <p className="text-gray-500 text-sm">Clicca per caricare un PDF (max 50MB)</p>
                )}
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_public} onChange={e => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 rounded border-gray-700 bg-[#111] text-lojack-red focus:ring-lojack-red" data-testid="manual-form-public" />
              <span className="text-gray-300 text-sm">Pubblico (link condivisibile)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.visible_to_dealers} onChange={e => setForm({ ...form, visible_to_dealers: e.target.checked })} className="w-4 h-4 rounded border-gray-700 bg-[#111] text-lojack-red focus:ring-lojack-red" data-testid="manual-form-dealer" />
              <span className="text-gray-300 text-sm">Visibile ai Dealer</span>
            </label>
          </div>

          <button type="submit" disabled={saving || uploading} className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="manual-form-submit">
            {uploading ? "Upload PDF in corso..." : saving ? "Salvataggio..." : isEdit ? "Salva Modifiche" : "Crea Manuale"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ManualsPage;
